import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { BackgroundGeolocation } from '@capgo/background-geolocation';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { TrackPoint, TrackWaypoint, WaypointIcon, CampingSpot, AppUser } from '../types';
import { getDistance } from '../services/geometryUtils';
import { logFeatureUsage } from '../services/firebaseService';
import { setCache, getCache } from '../services/cacheService';
import { useLocationGate } from '../contexts/LocationGateContext';
import { submitErrorReport } from '../services/errorReportService';
import { isDismissedRecently } from '../components/spots/AutoCheckInBanner';
import { classifyBackgroundGeoError } from '../services/gpsTrackingUtils';
import { runProximityCheckIn } from '../services/spotProximity';
import { scheduleProximityCheckInNotification } from '../services/checkInNotifications';

export type TrackingStatus = 'idle' | 'recording' | 'paused';

export interface DraftRecovery {
  points: TrackPoint[];
  elapsedSeconds: number;
}

export interface TrackStats {
  distance: number;     // meter
  duration: number;     // sekunder
  elevationGain: number;
  elevationLoss: number;
  currentAccuracy: number | null;
  currentSpeed: number | null; // m/s
}

const CACHE_KEY = 'active_track_points';
const CACHE_STATE_KEY = 'active_track_state';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 timer
const MIN_DISTANCE_M = 3;              // hopp over punkter < 3m fra forrige
const MAX_ACCURACY_M = 50;             // filtrer ut upresise avlesninger

const isNative = Capacitor.isNativePlatform();

export function useGpsTracking(spots: CampingSpot[] = [], currentUser?: AppUser | null) {
  const spotsRef = useRef(spots);
  const currentUserRef = useRef(currentUser);

  useEffect(() => { spotsRef.current = spots; }, [spots]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  const { gatedWatchPosition } = useLocationGate();
  const [status, setStatus] = useState<TrackingStatus>('idle');
  const [points, setPoints] = useState<TrackPoint[]>([]);
  const [trackWaypoints, setTrackWaypoints] = useState<TrackWaypoint[]>([]);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [lastAccuracy, setLastAccuracy] = useState<number | null>(null);
  const [lastSpeed, setLastSpeed] = useState<number | null>(null);

  const watchIdRef = useRef<number | null>(null);       // web geolocation watch ID
  const nativeActiveRef = useRef<boolean>(false);       // native GPS aktiv
  const watchGenRef = useRef<number>(0);                // bumpes ved hver stop – kansellerer in-flight start
  const startTimeRef = useRef<number>(0);
  const elapsedBeforePauseRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [durationSec, setDurationSec] = useState(0);

  // --- Wake Lock (web + native) ---
  async function acquireWakeLock() {
    if (isNative) {
      try { await KeepAwake.keepAwake(); } catch { /* ignore */ }
      return;
    }
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => { wakeLockRef.current = null; });
    } catch { /* Wake lock kan avslås av OS, ignorer */ }
  }

  function releaseWakeLock() {
    if (isNative) {
      KeepAwake.allowSleep().catch(() => {});
      return;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }

  // Rydd opp ved unmount
  useEffect(() => {
    return () => {
      stopWatch();
      releaseWakeLock();
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, []);

  // Re-acquire wake lock når appen kommer tilbake i forgrunnen (kun web)
  useEffect(() => {
    if (isNative) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'recording') {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [status]);

  // --- GPS-kontroll ---

  function stopWatch() {
    if (isNative) {
      // Bump generasjonen FØRST slik at en start() som er midt i sin await stop()
      // avbrytes når den våkner (se startNativeWatch). Kall alltid native stop()
      // – også hvis nativeActiveRef er false – i tilfelle en gammel native watcher
      // (fra en tidligere JS-sesjon) fortsatt lever.
      watchGenRef.current++;
      BackgroundGeolocation.stop().catch(() => {});
      nativeActiveRef.current = false;
    } else {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    }
  }

  function startTicker() {
    if (tickerRef.current) clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => {
      const elapsed = elapsedBeforePauseRef.current + (Date.now() - startTimeRef.current);
      setDurationSec(Math.floor(elapsed / 1000));
    }, 1000);
  }

  function stopTicker() {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  }

  // Felles punkt-behandler som brukes av både native og web
  const addPoint = useCallback((point: TrackPoint) => {
    setGpsError(null);
    setLastAccuracy(point.accuracy);
    setLastSpeed(point.speed ?? null);

    if (point.accuracy > MAX_ACCURACY_M) return;

    // Dwell-basert innsjekk-push (delt logikk – se services/spotProximity.ts).
    runProximityCheckIn(
      { latitude: point.latitude, longitude: point.longitude },
      spotsRef.current,
      {
        currentUserUid: currentUserRef.current?.uid,
        isDismissed: isDismissedRecently,
        notify: scheduleProximityCheckInNotification,
      },
    );

    setPoints(prev => {
      if (prev.length > 0) {
        const last = prev[prev.length - 1];
        const dist = getDistance(last.latitude, last.longitude, point.latitude, point.longitude);
        if (dist < MIN_DISTANCE_M) return prev;
      }
      const next = [...prev, point];
      // Mellomlagre HVERT punkt i tilfelle appen krasjer
      try { setCache(CACHE_KEY, next, CACHE_TTL); } catch { /* ignore quota errors */ }
      return next;
    });
  }, []);

  // Web geolocation callback
  const handlePosition = useCallback((pos: GeolocationPosition) => {
    addPoint({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      timestamp: pos.timestamp,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude !== null ? pos.coords.altitude : undefined,
      speed: pos.coords.speed !== null ? pos.coords.speed : undefined,
      heading: pos.coords.heading !== null ? pos.coords.heading : undefined,
    });
  }, [addPoint]);

  const handleError = useCallback((err: GeolocationPositionError) => {
    const msgs: Record<number, string> = {
      1: 'GPS-tilgang ble avslått. Aktiver posisjonstilgang i nettleserinnstillingene.',
      2: 'Posisjonen er ikke tilgjengelig akkurat nå.',
      3: 'GPS-forespørsel tok for lang tid.',
    };
    const msg = msgs[err.code] ?? `GPS-feil oppstod (kode ${err.code}): ${err.message || 'ukjent'}`;
    setGpsError(msg);
    submitErrorReport(
      'unhandled_error',
      `Web geolocation error [code ${err.code}]: ${err.message || msg}`,
      null,
    ).catch(() => { /* ignore */ });
  }, []);

  // Starter native bakgrunns-GPS. Native locationManager lever på plugin-
  // instansen og kan overleve JS-konteksten (OTA kald-start, WKWebView-reload,
  // iOS bakgrunns-relansering). Vi stopper derfor alltid en ev. gammel watcher
  // FØRST, slik at den nye sesjonen registrerer sin egen callback og start()
  // aldri avvises med ALREADY_STARTED mot en død callback.
  async function startNativeWatch() {
    // Marker aktiv SYNKRONT (før await) slik at en stopWatch() som kommer under
    // awaiten ser at det pågår sporing og faktisk kaller native stop().
    const gen = ++watchGenRef.current;
    nativeActiveRef.current = true;
    try { await BackgroundGeolocation.stop(); } catch { /* ingen aktiv sporing – ok */ }
    // Ble sporingen stoppet (pause/stop/reset/unmount) mens vi ventet? Da avbryter
    // vi denne starten så vi ikke etterlater en foreldreløs native watcher.
    if (gen !== watchGenRef.current) {
      BackgroundGeolocation.stop().catch(() => {});
      return;
    }
    BackgroundGeolocation.start(
      {
        backgroundTitle: 'Fricamp Turlogg',
        backgroundMessage: 'Sporer GPS-posisjon i bakgrunnen',
        requestPermissions: false,
        stale: false,
        distanceFilter: MIN_DISTANCE_M,
      },
      (location, error) => {
        if (error) {
          const code = error.code || 'UNKNOWN';
          const msg = error.message || String(error);
          const action = classifyBackgroundGeoError(code);
          if (action.kind === 'ignore-already-started') {
            // Sporing kjører allerede native – ønsket tilstand. Ikke vis feil.
            nativeActiveRef.current = true;
            setGpsError(null);
            return;
          }
          if (action.kind === 'permission') {
            setGpsError('GPS-tilgang ble avslått. Aktiver "Tillat hele tiden" i Innstillinger → Apper → Fricamp → Tillatelser → Posisjon.');
            BackgroundGeolocation.openSettings();
          } else {
            setGpsError(`GPS-feil (${code}): ${msg}`);
          }
          if (action.report) {
            submitErrorReport(
              'unhandled_error',
              `BackgroundGeolocation callback error [${code}]: ${msg}`,
              error.stack || null,
            ).catch(() => { /* ignore */ });
          }
          return;
        }
        if (!location) return;
        addPoint({
          latitude: location.latitude,
          longitude: location.longitude,
          timestamp: location.time ?? Date.now(),
          accuracy: location.accuracy,
          altitude: location.altitude !== null ? location.altitude : undefined,
          speed: location.speed !== null ? location.speed : undefined,
          heading: location.bearing !== null ? location.bearing : undefined,
        });
      }
    ).catch((e: any) => {
      const msg = e?.message || String(e);
      const code = e?.code ? ` [${e.code}]` : '';
      setGpsError(`Kunne ikke starte bakgrunns-GPS${code}: ${msg}`);
      submitErrorReport(
        'unhandled_error',
        `BackgroundGeolocation.start() rejected${code}: ${msg}`,
        e?.stack || null,
      ).catch(() => { /* ignore */ });
    });
  }

  // Starter native eller web GPS
  function startGpsWatch() {
    if (isNative) {
      startNativeWatch();
    } else {
      if (!navigator.geolocation) {
        setGpsError('GPS er ikke støttet i denne nettleseren.');
        return;
      }
      watchIdRef.current = gatedWatchPosition(
        handlePosition,
        handleError,
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
      );
    }
  }

  const addTrackWaypoint = useCallback((icon: WaypointIcon, name: string) => {
    const lastPt = points[points.length - 1];
    if (!lastPt) return;
    const wp: TrackWaypoint = {
      latitude: lastPt.latitude,
      longitude: lastPt.longitude,
      name,
      icon,
      timestamp: Date.now(),
    };
    setTrackWaypoints(prev => [...prev, wp]);
  }, [points]);

  const start = useCallback(() => {
    try {
      setGpsError(null);
      startTimeRef.current = Date.now();
      elapsedBeforePauseRef.current = 0;
      setDurationSec(0);
      setPoints([]);
      setTrackWaypoints([]);

      logFeatureUsage('trip_log');
      startGpsWatch();
      setStatus('recording');
      startTicker();
      acquireWakeLock();
    } catch (e: any) {
      const msg = e?.message || String(e);
      setGpsError(`Kunne ikke starte opptak: ${msg}`);
      submitErrorReport(
        'unhandled_error',
        `useGpsTracking.start() threw: ${msg}`,
        e?.stack || null,
      ).catch(() => { /* ignore */ });
    }
  }, [handlePosition, handleError, addPoint]);

  const pause = useCallback(() => {
    stopWatch();
    stopTicker();
    releaseWakeLock();
    elapsedBeforePauseRef.current += Date.now() - startTimeRef.current;
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    startTimeRef.current = Date.now();
    startGpsWatch();
    setStatus('recording');
    startTicker();
    acquireWakeLock();
  }, [handlePosition, handleError, addPoint]);

  const stop = useCallback(() => {
    stopWatch();
    stopTicker();
    releaseWakeLock();
    elapsedBeforePauseRef.current += Date.now() - startTimeRef.current;
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    stopWatch();
    stopTicker();
    releaseWakeLock();
    setStatus('idle');
    setPoints([]);
    setTrackWaypoints([]);
    setDurationSec(0);
    elapsedBeforePauseRef.current = 0;
    try { localStorage.removeItem('villmark_cache_' + CACHE_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem('villmark_cache_' + CACHE_STATE_KEY); } catch { /* ignore */ }
  }, []);

  // Beregn stats fra punktene
  const stats = useMemo<TrackStats>(() => {
    let distance = 0;
    let elevationGain = 0;
    let elevationLoss = 0;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      distance += getDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);

      if (prev.altitude !== undefined && curr.altitude !== undefined) {
        const diff = curr.altitude - prev.altitude;
        if (diff > 0) elevationGain += diff;
        else elevationLoss += Math.abs(diff);
      }
    }

    return {
      distance,
      duration: durationSec,
      elevationGain: Math.round(elevationGain),
      elevationLoss: Math.round(elevationLoss),
      currentAccuracy: lastAccuracy,
      currentSpeed: lastSpeed,
    };
  }, [points, durationSec, lastAccuracy, lastSpeed]);

  // Cache sporingstilstand hvert sekund under aktiv sporing
  useEffect(() => {
    if (status === 'idle') return;
    try {
      setCache(CACHE_STATE_KEY, {
        status,
        elapsedBeforePause: elapsedBeforePauseRef.current,
        startTime: startTimeRef.current,
        savedAt: Date.now(),
      }, CACHE_TTL);
    } catch { /* ignore */ }
  }, [status, durationSec]);

  // Sjekk ved oppstart om det finnes en uferdig tur fra forrige sesjon
  const draftRecovery = useMemo<DraftRecovery | null>(() => {
    try {
      const pts = getCache(CACHE_KEY) as TrackPoint[] | null;
      if (!pts || pts.length < 2) return null;
      const state = getCache(CACHE_STATE_KEY) as {
        elapsedBeforePause: number;
        startTime: number;
        savedAt: number;
      } | null;
      const elapsedSeconds = state
        ? Math.floor((state.elapsedBeforePause + (state.savedAt - state.startTime)) / 1000)
        : 0;
      return { points: pts, elapsedSeconds };
    } catch {
      return null;
    }
  }, []);

  // Gjenopprett uferdig tur fra cache
  const restoreDraft = useCallback((draft: DraftRecovery) => {
    setPoints(draft.points);
    setDurationSec(draft.elapsedSeconds);
    elapsedBeforePauseRef.current = draft.elapsedSeconds * 1000;
    startTimeRef.current = Date.now();
    setStatus('paused');
  }, []);

  return {
    status,
    points,
    trackWaypoints,
    stats,
    gpsError,
    draftRecovery,
    restoreDraft,
    addTrackWaypoint,
    start,
    pause,
    resume,
    stop,
    reset,
  };
}
